import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Form,
  Button,
  Select,
  Table,
  message,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import DateRangeFilter from '../../components/DateRangeFilter';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title } = Typography;

const Reconciliation = () => {
  const [form] = Form.useForm();
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [incomeJobs, setIncomeJobs] = useState([]);
  const [expenseJobs, setExpenseJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]); // Jobs shown in dropdown based on PL filter
  const [plPositionFilter, setPlPositionFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all'); // Channel filter
  const [channels, setChannels] = useState([]); // Available channels
  const [dateFilter, setDateFilter] = useState({}); // Filter by job.createdAt
  const [loading, setLoading] = useState(false);
  const [selectValue, setSelectValue] = useState(undefined); // Controlled value for Select
  const selectRef = useRef(null); // Ref to control the Select component

  useEffect(() => {
    fetchAllJobs();
  }, []);

  useEffect(() => {
    console.log('Filtering jobs. plPositionFilter:', plPositionFilter);
    console.log('Channel filter:', channelFilter);
    console.log('Date filter:', dateFilter);
    console.log('allJobs length:', allJobs.length);
    console.log('incomeJobs length:', incomeJobs.length);
    console.log('expenseJobs length:', expenseJobs.length);

    let filtered = allJobs;

    // Filter by PL position
    if (plPositionFilter === 'income') {
      filtered = incomeJobs;
      console.log('Set filtered jobs to income:', incomeJobs.length);
    } else if (plPositionFilter === 'expense') {
      filtered = expenseJobs;
      console.log('Set filtered jobs to expense:', expenseJobs.length);
    } else {
      filtered = allJobs;
      console.log('Set filtered jobs to all:', allJobs.length);
    }

    // Filter by channel
    if (channelFilter !== 'all') {
      filtered = filtered.filter(job => job.channel === channelFilter);
      console.log('After channel filter:', filtered.length);
    }

    // Filter by job.createdAt (using DateRangeFilter)
    if (dateFilter) {
      let start = null;
      let end = null;

      // Custom date range
      if (dateFilter.dateRange && dateFilter.dateRange.length === 2) {
        const [startDate, endDate] = dateFilter.dateRange;
        if (startDate && endDate) {
          start = startDate.startOf('day');
          end = endDate.endOf('day');
        }
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined' &&
        dateFilter.month !== 'custom'
      ) {
        // Year + month mode
        start = dayjs().year(dateFilter.year).month(dateFilter.month).startOf('month');
        end = dayjs().year(dateFilter.year).month(dateFilter.month).endOf('month');
      }

      if (start && end) {
        filtered = filtered.filter(job => {
          if (!job.createdAt) return false;
          const created = dayjs(job.createdAt);
          return (
            (created.isAfter(start) && created.isBefore(end)) ||
            created.isSame(start, 'day') ||
            created.isSame(end, 'day')
          );
        });
        console.log('After date filter:', filtered.length);
      }
    }

    setFilteredJobs(filtered);
  }, [plPositionFilter, channelFilter, allJobs, incomeJobs, expenseJobs, dateFilter]);

  const handleDateFilterChange = (newValue) => {
    setDateFilter(newValue || {});
  };

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      const [jobsResponse, channelsResponse] = await Promise.all([
        apiService.jobs.getReconciliationJobs(),
        apiService.channels.getAll()
      ]);
      
      const jobs = jobsResponse.data.data || [];
      const channelsData = channelsResponse.data || [];
      
      console.log('Reconciliation jobs fetched:', jobs.length);
      console.log('Sample job:', jobs[0]);
      setAllJobs(jobs);
      setChannels(channelsData);
      
      // Separate income and expense jobs
      const income = jobs.filter(job => job.plPosition === 'income');
      const expense = jobs.filter(job => job.plPosition === 'expense');
      
      console.log('Income jobs:', income.length);
      console.log('Expense jobs:', expense.length);
      
      setIncomeJobs(income);
      setExpenseJobs(expense);
    } catch (error) {
      message.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveJob = (jobId) => {
    setSelectedJobs(prev => prev.filter(job => job._id !== jobId));
  };

  const handleBulkUpdate = async (values) => {
    if (selectedJobs.length === 0) {
      message.error('Please select at least one job');
      return;
    }

    try {
      setLoading(true);

      // Extract job IDs from selected jobs
      const jobIds = selectedJobs.map(job => job._id);

      // Make a single batch update call
      const response = await apiService.jobs.batchUpdatePayment(jobIds, {
        paymentStatus: values.paymentStatus
      });

      message.success(response.data.message || `Successfully updated ${selectedJobs.length} job(s)`);
      setSelectedJobs([]);
      form.resetFields();
      fetchAllJobs(); // Refresh the job list

    } catch (error) {
      console.error('Batch update error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Booking Reference',
      dataIndex: 'bookingReference',
      key: 'bookingReference',
      render: (ref) => ref || 'N/A'
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      render: (name) => name || 'N/A'
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel'
    },
    {
      title: 'PL Position',
      dataIndex: 'plPosition',
      key: 'plPosition',
      render: (position) => (
        <span style={{
          color: position === 'income' ? 'green' : 'red',
          fontWeight: 'bold'
        }}>
          {position?.toUpperCase() || 'N/A'}
        </span>
      )
    },
    {
      title: 'Amount',
      dataIndex: ['payment', 'amount'],
      key: 'amount',
      render: (amount) => amount ? `£${amount.toFixed(2)}` : 'N/A'
    },
    {
      title: 'Current Status',
      dataIndex: ['payment', 'status'],
      key: 'status',
      render: (status) => (
        <span style={{
          color: status === 'paid' ? 'green' : status === 'overdue' ? 'red' : 'orange',
          fontWeight: 'bold'
        }}>
          {status?.toUpperCase() || 'N/A'}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveJob(record._id)}
        >
          Remove
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={3}>Payment Reconciliation</Title>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card title="Upload Statement File" size="small">
              <Form layout="inline" onFinish={async (vals) => {}}>
                <Form.Item>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        setLoading(true);
                        const res = await apiService.jobs.uploadReconciliationFile(file);
                        if (res?.data?.success) {
                          message.success(res.data.message || 'Reconciliation processed');
                        } else {
                          message.success('Reconciliation processed');
                        }
                        await fetchAllJobs();
                      } catch (err) {
                        message.error(err?.response?.data?.message || 'Failed to process file');
                      } finally {
                        setLoading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <div style={{ color: '#666' }}>Expected column: Booking Reference — all matching jobs will be marked as Paid</div>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card title="Add Jobs from Dropdown" size="small">
              <Form form={form} layout="vertical">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="plPositionFilter" label="Filter by PL Position">
                      <Select
                        placeholder="Select PL position filter"
                        value={plPositionFilter}
                        onChange={setPlPositionFilter}
                      >
                        <Option value="all">All Jobs</Option>
                        <Option value="income">Income Jobs Only</Option>
                        <Option value="expense">Expense Jobs Only</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="channelFilter" label="Filter by Channel">
                      <Select
                        placeholder="Select channel filter"
                        value={channelFilter}
                        onChange={setChannelFilter}
                        allowClear
                      >
                        <Option value="all">All Channels</Option>
                        {channels.map(channel => (
                          <Option key={channel._id || channel.name} value={channel.name}>
                            {channel.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Filter by Job Created At">
                      <DateRangeFilter
                        value={dateFilter}
                        onChange={handleDateFilterChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="jobSelect" label="Select Job">
                  <Select
                    ref={selectRef}
                    placeholder="Select a job to add"
                    showSearch
                    allowClear
                    value={selectValue}
                    onChange={(value) => {
                      if (value) {
                        const job = filteredJobs.find(j => j._id === value);
                        if (job && !selectedJobs.some(selected => selected._id === job._id)) {
                          setSelectedJobs(prev => [...prev, job]);
                        } else if (job) {
                          message.warning('Job already selected');
                        }
                      }
                      // Use setTimeout to ensure clearing happens after the current render cycle
                      setTimeout(() => {
                        setSelectValue(undefined);
                        form.setFieldsValue({ jobSelect: undefined });
                        if (selectRef.current) {
                          selectRef.current.blur();
                        }
                      }, 0);
                    }}
                    filterOption={(input, option) => {
                      const optionText = option?.children;
                      if (!optionText) return false;
                      
                      // Convert to string and handle different types
                      const text = typeof optionText === 'string' ? optionText : String(optionText);
                      return text.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {filteredJobs
                      .filter(job => !selectedJobs.some(selected => selected._id === job._id))
                      .map(job => (
                        <Option key={job._id} value={job._id}>
                          {job.bookingReference || job.reference} - {job.customer?.name} 
                          ({job.plPosition === 'income' ? 'Income' : 'Expense'})
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Card title={`Selected Jobs (${selectedJobs.length})`} style={{ marginBottom: 24 }}>
          <Table
            columns={columns}
            dataSource={selectedJobs}
            rowKey="_id"
            pagination={false}
            size="small"
          />
        </Card>

        {selectedJobs.length > 0 && (
          <Card title="Bulk Update Payment Status">
            <Form layout="vertical" onFinish={handleBulkUpdate}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="paymentStatus"
                    label="Payment Status"
                    rules={[{ required: true, message: 'Please select payment status' }]}
                  >
                    <Select placeholder="Select payment status">
                      <Option value="pending">Pending</Option>
                      <Option value="paid">Paid</Option>
                      <Option value="overdue">Overdue</Option>
                      <Option value="cancelled">Cancelled</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                >
                  Update {selectedJobs.length} Job(s)
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default Reconciliation; 